import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Eye, Check, X, AlertCircle, Search, ChevronUp, ChevronDown, FileText, Trash2 } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import ExportButtons from './ExportButtons';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';

const TrainerListManagerNew = () => {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [filter, setFilter] = useState('all'); // all, pending, active, active_not_listed, pending_listing, listed
  
  // Modals
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewTrainer, setPreviewTrainer] = useState(null);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [trainerToReject, setTrainerToReject] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [requestChangesModalOpen, setRequestChangesModalOpen] = useState(false);
  const [trainerForChanges, setTrainerForChanges] = useState(null);
  const [missingItems, setMissingItems] = useState([]);
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [rejectListingModalOpen, setRejectListingModalOpen] = useState(false);
  const [trainerToRejectListing, setTrainerToRejectListing] = useState(null);
  const [listingRejectionReason, setListingRejectionReason] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [trainerToDelete, setTrainerToDelete] = useState(null);
  const [comparisonModalOpen, setComparisonModalOpen] = useState(false);
  const [profileHistory, setProfileHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // One-time production data migration (only surfaces when there is data to import)
  const [migrationStatus, setMigrationStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  useEffect(() => {
    fetchTrainers();
    fetchMigrationStatus();
  }, []);

  const fetchMigrationStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/migration/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) setMigrationStatus(await res.json());
    } catch (e) {
      // silently ignore — migration banner just won't show
    }
  };

  const runMigration = async () => {
    setMigrating(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_BASE}/api/admin/migration/import-trainers`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok && data.success) {
        toast({ title: 'Migration complete', description: `Imported ${data.stats.inserted} trainers (skipped ${data.stats.skipped_existing_email + data.stats.skipped_already}).` });
        await fetchMigrationStatus();
        await fetchTrainers();
      } else {
        toast({ title: 'Migration failed', description: data.error || 'Unknown error', variant: 'destructive' });
      }
    } catch (e) {
      toast({ title: 'Migration error', description: e.message, variant: 'destructive' });
    } finally {
      setMigrating(false);
    }
  };

  const fetchTrainers = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use admin endpoint to get ALL trainers (including pending)
      const response = await fetch(`${API_BASE}/api/admin/trainers?limit=1000`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const responseData = await response.json();
        // API returns { trainers: [...], total, page, pages }
        const trainersData = responseData.trainers || [];
        console.log('[TrainerListManagerNew] Fetched trainers:', trainersData.length);
        setTrainers(trainersData);
      }
    } catch (error) {
      console.error('Error fetching trainers:', error);
      toast({ title: 'Error', description: 'Failed to load trainers', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  // Approve trainer (first approval - account activation)
  const handleApprove = async (trainerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/trainers/${trainerId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ approved: true })
      });

      if (response.ok) {
        toast({ 
          title: 'Success', 
          description: 'Trainer account approved. Trainer can now request homepage listing.' 
        });
        fetchTrainers();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to approve trainer', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve trainer', variant: 'destructive' });
    }
  };

  // Approve listing (second approval - homepage listing)
  const handleApproveListing = async (trainerId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/trainers/${trainerId}/approve-listing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Trainer approved for homepage listing' });
        fetchTrainers();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to approve listing', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to approve listing', variant: 'destructive' });
    }
  };

  // Reject trainer
  const handleRejectClick = (trainer) => {
    setTrainerToReject(trainer);
    setRejectionReason('');
    setRejectModalOpen(true);
  };

  const handleRejectConfirm = async () => {
    if (!rejectionReason.trim()) {
      toast({ title: 'Error', description: 'Please provide a reason for rejection', variant: 'destructive' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/trainers/${trainerToReject.id}/reject`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: rejectionReason })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Trainer application rejected' });
        setRejectModalOpen(false);
        setTrainerToReject(null);
        setRejectionReason('');
        fetchTrainers();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to reject trainer', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject trainer', variant: 'destructive' });
    }
  };

  // Request changes
  const handleRequestChangesClick = (trainer) => {
    setTrainerForChanges(trainer);
    setMissingItems([]);
    setAdditionalNotes('');
    setRequestChangesModalOpen(true);
  };

  const handleMissingItemToggle = (item) => {
    setMissingItems(prev => 
      prev.includes(item) 
        ? prev.filter(i => i !== item)
        : [...prev, item]
    );
  };

  const handleRequestChangesConfirm = async () => {
    if (missingItems.length === 0 && !additionalNotes.trim()) {
      toast({ title: 'Error', description: 'Please select at least one item or provide notes', variant: 'destructive' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/trainers/${trainerForChanges.id}/request-changes`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          missing_items: missingItems,
          additional_notes: additionalNotes 
        })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Change request sent to trainer' });
        setRequestChangesModalOpen(false);
        setTrainerForChanges(null);
        setMissingItems([]);
        setAdditionalNotes('');
        fetchTrainers();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to send change request', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to send change request', variant: 'destructive' });
    }
  };

  // Reject listing request
  const handleRejectListingClick = (trainer) => {
    setTrainerToRejectListing(trainer);
    setListingRejectionReason('');
    setRejectListingModalOpen(true);
  };

  const handleRejectListingConfirm = async () => {
    if (!listingRejectionReason.trim()) {
      toast({ title: 'Error', description: 'Please provide a reason for rejection', variant: 'destructive' });
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/trainers/${trainerToRejectListing.id}/reject-listing`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason: listingRejectionReason })
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Listing request rejected' });
        setRejectListingModalOpen(false);
        setTrainerToRejectListing(null);
        setListingRejectionReason('');
        fetchTrainers();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to reject listing', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject listing', variant: 'destructive' });
    }
  };

  // Preview trainer details
  const handlePreview = (trainer) => {
    setPreviewTrainer(trainer);
    setPreviewModalOpen(true);
  };

  // Delete trainer
  const handleDeleteClick = (trainer) => {
    setTrainerToDelete(trainer);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!trainerToDelete) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/trainers/${trainerToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        toast({ title: 'Success', description: 'Trainer deleted successfully' });
        setDeleteConfirmOpen(false);
        setTrainerToDelete(null);
        fetchTrainers();
      } else {
        const error = await response.json();
        toast({ title: 'Error', description: error.detail || 'Failed to delete trainer', variant: 'destructive' });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete trainer', variant: 'destructive' });
    }
  };

  // Delete trainer photo (admin)
  const handleDeletePhoto = async (trainerId, photoUrl) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) return;
    
    try {
      const token = localStorage.getItem('token');
      // Extract filename from URL (e.g., /api/trainer/photos/abc123.jpg -> abc123.jpg)
      const filename = photoUrl.split('/').pop();
      
      const response = await fetch(`${API_BASE}/api/admin/trainers/${trainerId}/photos/${filename}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Photo deleted successfully' });
        // Update preview trainer photos locally
        if (previewTrainer) {
          setPreviewTrainer({
            ...previewTrainer,
            photos: previewTrainer.photos.filter(p => p !== photoUrl)
          });
        }
        fetchTrainers();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete photo', variant: 'destructive' });
    }
  };

  // Delete trainer certificate (admin)
  const handleDeleteCertificate = async (trainerId, certUrl, certFilename) => {
    if (!window.confirm('Are you sure you want to delete this certificate?')) return;
    
    try {
      const token = localStorage.getItem('token');
      // Extract filename from URL or use provided filename
      const filename = certFilename || certUrl.split('/').pop();
      
      const response = await fetch(`${API_BASE}/api/admin/trainers/${trainerId}/certificates/${filename}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast({ title: 'Success', description: 'Certificate deleted successfully' });
        // Update preview trainer certificates locally
        if (previewTrainer) {
          setPreviewTrainer({
            ...previewTrainer,
            certificates: previewTrainer.certificates.filter(c => c.url !== certUrl)
          });
        }
        fetchTrainers();
      } else {
        throw new Error('Failed to delete');
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to delete certificate', variant: 'destructive' });
    }
  };

  // Fetch profile history for comparison
  const fetchProfileHistory = async (trainerId) => {
    setLoadingHistory(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/admin/trainers/${trainerId}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('History data received:', data);
        
        // Backend returns: { current: {...}, history: [...sorted newest first], total_versions: N }
        const allVersions = [];
        
        // Add current version first (newest)
        if (data.current) {
          allVersions.push({
            ...data.current,
            timestamp: new Date().toISOString(),
            note: 'Current Version (Latest)'
          });
        }
        
        // Add history (already sorted by backend, newest first)
        if (data.history && data.history.length > 0) {
          data.history.forEach((entry, idx) => {
            allVersions.push({
              ...entry,
              note: entry.note || `Version ${idx + 2}` // +2 because current is version 1
            });
          });
        }
        
        console.log('All versions (newest to oldest):', allVersions);
        setProfileHistory(allVersions);
        setComparisonModalOpen(true);
        
        // Mark changes as viewed
        try {
          await fetch(`${API_BASE}/api/admin/trainers/${trainerId}/mark-changes-viewed`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          // Update local state to remove the notification badge
          setTrainers(prevTrainers =>
            prevTrainers.map(t =>
              t.id === trainerId ? { ...t, has_unviewed_changes: false } : t
            )
          );
        } catch (error) {
          console.error('Error marking changes as viewed:', error);
        }
      } else {
        toast({ 
          title: 'Error', 
          description: 'Failed to load profile history', 
          variant: 'destructive' 
        });
      }
    } catch (error) {
      console.error('Error fetching history:', error);
      toast({ 
        title: 'Error', 
        description: 'Failed to load profile history', 
        variant: 'destructive' 
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Filter and sort trainers
  const filteredTrainers = trainers
    .filter(trainer => {
      // Apply filter
      if (filter === 'pending' && trainer.status !== 'pending') return false;
      if (filter === 'active' && trainer.status !== 'active') return false;
      if (filter === 'active_not_listed' && !(trainer.status === 'active' && !trainer.listing_status)) return false;
      if (filter === 'pending_listing' && trainer.listing_status !== 'pending_listing') return false;
      if (filter === 'listed' && trainer.listing_status !== 'listed') return false;
      
      // Apply search
      const searchLower = searchTerm.toLowerCase();
      return (
        trainer.name?.toLowerCase().includes(searchLower) ||
        trainer.email?.toLowerCase().includes(searchLower) ||
        trainer.phone?.toLowerCase().includes(searchLower) ||
        trainer.city?.toLowerCase().includes(searchLower)
      );
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'created_at') {
        aVal = new Date(a.created_at || 0);
        bVal = new Date(b.created_at || 0);
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronUp className="w-4 h-4 text-gray-300" />;
    return sortDirection === 'asc' 
      ? <ChevronUp className="w-4 h-4 text-blue-600" />
      : <ChevronDown className="w-4 h-4 text-blue-600" />;
  };

  const pendingCount = trainers.filter(t => t.status === 'pending').length;
  const activeCount = trainers.filter(t => t.status === 'active').length;
  const activeNotListedCount = trainers.filter(t => t.status === 'active' && !t.listing_status).length;
  const pendingListingCount = trainers.filter(t => t.listing_status === 'pending_listing').length;
  const listedCount = trainers.filter(t => t.listing_status === 'listed').length;

  if (loading) {
    return <div className="p-6 text-center">Loading trainers...</div>;
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Trainer List</h2>
        <p className="text-gray-600 dark:text-gray-400 mt-1">Manage and monitor all trainers</p>
      </div>

      {/* One-time production data migration banner (auto-hides when nothing to import) */}
      {migrationStatus && migrationStatus.remaining > 0 && (
        <Card className="mb-6 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 dark:border-emerald-700" data-testid="migration-banner">
          <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-emerald-600 dark:text-emerald-400 mt-0.5" />
              <div>
                <div className="font-semibold text-emerald-900 dark:text-emerald-300">
                  Import migrated trainers from trainerlist.com
                </div>
                <div className="text-sm text-emerald-700 dark:text-emerald-400">
                  {migrationStatus.remaining} of {migrationStatus.bundled_records} trainers are ready to import into this site. This is safe to run once.
                </div>
              </div>
            </div>
            <Button
              onClick={runMigration}
              disabled={migrating}
              className="bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
              data-testid="run-migration-btn"
            >
              {migrating ? 'Importing…' : `Import ${migrationStatus.remaining} Trainers`}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6">
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilter('all')}>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Total Trainers</div>
            <div className="text-3xl font-bold">{trainers.length}</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilter('pending')}>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Pending Approval</div>
            <div className="text-3xl font-bold text-yellow-600">{pendingCount}</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilter('active_not_listed')}>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Active (Not Listed)</div>
            <div className="text-3xl font-bold text-blue-600">{activeNotListedCount}</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilter('pending_listing')}>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Listing Requests</div>
            <div className="text-3xl font-bold text-orange-600">{pendingListingCount}</div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:shadow-md" onClick={() => setFilter('listed')}>
          <CardContent className="p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">Listed on Homepage</div>
            <div className="text-3xl font-bold text-green-600">{listedCount}</div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Search */}
      <div className="mb-6 flex flex-col sm:flex-row gap-4">
        <div className="flex gap-2 flex-wrap">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            size="sm"
          >
            All
          </Button>
          <Button 
            variant={filter === 'pending' ? 'default' : 'outline'}
            onClick={() => setFilter('pending')}
            size="sm"
          >
            🔶 Pending ({pendingCount})
          </Button>
          <Button 
            variant={filter === 'active_not_listed' ? 'default' : 'outline'}
            onClick={() => setFilter('active_not_listed')}
            size="sm"
          >
            ⚪ Active Not Listed ({activeNotListedCount})
          </Button>
          <Button 
            variant={filter === 'pending_listing' ? 'default' : 'outline'}
            onClick={() => setFilter('pending_listing')}
            size="sm"
          >
            🔔 Listing Requests ({pendingListingCount})
          </Button>
          <Button 
            variant={filter === 'listed' ? 'default' : 'outline'}
            onClick={() => setFilter('listed')}
            size="sm"
          >
            ✅ Listed ({listedCount})
          </Button>
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search trainers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Export Buttons */}
      <ExportButtons 
        data={filteredTrainers}
        columns={[
          { header: 'ID', accessor: (row) => row.id || 'N/A' },
          { header: 'Name', accessor: (row) => row.name || 'N/A' },
          { header: 'Phone', accessor: (row) => row.phone || 'N/A' },
          { header: 'Email', accessor: (row) => row.email || 'N/A' },
          { header: 'City', accessor: (row) => row.city || 'N/A' },
          { header: 'Status', accessor: (row) => row.status || 'N/A' },
          { header: 'Listing Status', accessor: (row) => row.listing_status || 'N/A' },
          { header: 'Created At', accessor: (row) => row.created_at ? new Date(row.created_at).toLocaleDateString() : 'N/A' }
        ]}
        filename="trainer-list"
      />

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ID
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    NAME
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    PHONE
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    EMAIL
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    CITY
                  </th>
                  <th 
                    className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-2">
                      REGISTER DATE
                      <SortIcon field="created_at" />
                    </div>
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    STATUS
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    ACTION
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                {filteredTrainers.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No trainers found
                    </td>
                  </tr>
                ) : (
                  filteredTrainers.map((trainer, index) => (
                    <tr key={trainer.id} className="hover:bg-gray-50 dark:hover:bg-gray-600 dark:bg-gray-800">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                        {index + 1}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900 dark:text-white">{trainer.name || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {trainer.phone || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {trainer.email || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {trainer.city || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                        {trainer.created_at ? new Date(trainer.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-col gap-1">
                          <Badge 
                            className={
                              trainer.status === 'active' 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' 
                                : trainer.status === 'pending'
                                ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
                                : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                            }
                          >
                            {trainer.status || 'pending'}
                          </Badge>
                          {trainer.has_unviewed_changes && (
                            <Badge className="bg-red-500 text-white animate-pulse">
                              🔔 NEW CHANGES
                            </Badge>
                          )}
                          {trainer.listing_status === 'pending_listing' && (
                            <Badge className="bg-orange-100 text-orange-800 animate-pulse">
                              🔔 Listing Request
                            </Badge>
                          )}
                          {trainer.listing_status === 'listed' && (
                            <Badge className="bg-emerald-100 text-emerald-800">
                              ✅ Listed
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <div className="flex gap-1 flex-wrap">
                          {/* View Details - always visible */}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handlePreview(trainer)}
                            title="View Details"
                            className={trainer.has_unviewed_changes ? "animate-pulse" : ""}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>

                          {/* View Changes - always show if trainer is active or has updates */}
                          {(trainer.status === 'active' || trainer.has_unviewed_changes) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className={trainer.has_unviewed_changes 
                                ? "text-red-600 hover:text-red-700 animate-pulse" 
                                : "text-blue-600 hover:text-blue-700"}
                              onClick={() => fetchProfileHistory(trainer.id)}
                              title={trainer.has_unviewed_changes ? "New Changes - View History" : "View Profile History"}
                            >
                              {trainer.has_unviewed_changes ? '🔔' : '📋'}
                            </Button>
                          )}
                          
                          {/* Pending trainer actions */}
                          {trainer.status === 'pending' && (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-green-600 hover:text-green-700"
                                onClick={() => handleApprove(trainer.id)}
                                title="Approve Trainer"
                              >
                                <Check className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700"
                                onClick={() => handleRejectClick(trainer)}
                                title="Reject Trainer"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          
                          {/* Active (Not Listed) trainer actions */}
                          {trainer.status === 'active' && !trainer.listing_status && (
                            <span className="text-xs text-green-600 font-medium">
                              ✓ Active
                            </span>
                          )}
                          
                          {/* Listing request actions */}
                          {trainer.listing_status === 'pending_listing' && (
                            <span className="text-xs text-green-600 font-medium">
                              ✓ Active
                            </span>
                          )}

                          {/* Delete button - always available */}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:text-gray-300"
                            onClick={() => handleDeleteClick(trainer)}
                            title="Delete from Database"
                          >
                            🗑️
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Footer Stats */}
      <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
        Showing {filteredTrainers.length} of {trainers.length} trainers
      </div>

      {/* Preview Modal */}
      <Dialog open={previewModalOpen} onOpenChange={setPreviewModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Trainer Details</DialogTitle>
            <DialogDescription>Complete information about the trainer</DialogDescription>
          </DialogHeader>
          {previewTrainer && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-600 dark:text-gray-400">Name</Label>
                  <p className="font-medium">{previewTrainer.name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-400">Email</Label>
                  <p className="font-medium">{previewTrainer.email || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-400">Phone</Label>
                  <p className="font-medium">{previewTrainer.phone || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-400">City</Label>
                  <p className="font-medium">{previewTrainer.city || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-400">Price/Hour</Label>
                  <p className="font-medium">${previewTrainer.pricePerHour || previewTrainer.price_per_hour || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-gray-600 dark:text-gray-400">Status</Label>
                  <Badge className={previewTrainer.status === 'active' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'}>
                    {previewTrainer.status}
                  </Badge>
                </div>
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-400">Bio</Label>
                <p className="mt-2 text-sm">{previewTrainer.bio || 'No bio provided'}</p>
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-400">Specializations</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {previewTrainer.specializations?.map((spec, idx) => (
                    <Badge key={spec} variant="outline">{spec}</Badge>
                  )) || 'None'}
                </div>
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-400">Training Photos ({previewTrainer.photos?.length || 0})</Label>
                <div className="grid grid-cols-3 gap-4 mt-2">
                  {previewTrainer.photos?.length > 0 ? previewTrainer.photos.map((photo, idx) => {
                    // Handle different photo formats (string URL or object with url property)
                    const photoUrl = typeof photo === 'string' ? photo : photo?.url || '';
                    const fullPhotoUrl = photoUrl.startsWith('http') ? photoUrl : `${API_BASE}${photoUrl}`;
                    return (
                      <div key={idx} className="relative border rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800">
                        <img 
                          src={fullPhotoUrl} 
                          alt={`Photo ${idx + 1}`} 
                          className="w-full h-32 object-cover"
                          onError={(e) => { e.target.src = 'https://via.placeholder.com/200x150?text=Photo'; }}
                        />
                        <button
                          onClick={() => handleDeletePhoto(previewTrainer.id, photoUrl)}
                          className="absolute top-2 right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors"
                          title="Delete photo"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  }) : <p className="text-sm text-gray-500 dark:text-gray-400 col-span-3">No photos uploaded</p>}
                </div>
              </div>

              <div>
                <Label className="text-gray-600 dark:text-gray-400">Certificates ({previewTrainer.certificates?.length || 0})</Label>
                <div className="space-y-2 mt-2">
                  {previewTrainer.certificates?.length > 0 ? previewTrainer.certificates.map((cert, idx) => {
                    // Handle different certificate formats (string or object)
                    const certUrl = typeof cert === 'string' ? cert : cert?.url || '';
                    const certFilename = typeof cert === 'string' ? cert.split('/').pop() : cert?.filename || `Certificate ${idx + 1}`;
                    return (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border">
                        <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                        <a 
                          href={certUrl.startsWith('http') ? certUrl : `${API_BASE}${certUrl}`} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 hover:underline text-sm flex-1 truncate"
                        >
                          {certFilename}
                        </a>
                        <button
                          onClick={() => handleDeleteCertificate(previewTrainer.id, certUrl, certFilename)}
                          className="bg-red-500 hover:bg-red-600 text-white rounded-full p-1.5 shadow-lg transition-colors flex-shrink-0"
                          title="Delete certificate"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  }) : <p className="text-sm text-gray-500 dark:text-gray-400">No certificates uploaded</p>}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Trainer Modal */}
      <Dialog open={rejectModalOpen} onOpenChange={setRejectModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Trainer Application</DialogTitle>
            <DialogDescription>Please provide a reason for rejection</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Reason for rejection..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectModalOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectConfirm}>Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Changes Modal */}
      <Dialog open={requestChangesModalOpen} onOpenChange={setRequestChangesModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Changes</DialogTitle>
            <DialogDescription>Select missing items or provide additional notes</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Missing Items</Label>
              <div className="space-y-2 mt-2">
                {['Profile Photo', 'Training Photos', 'Certificates', 'Bio', 'Specializations', 'Pricing'].map(item => (
                  <div key={item} className="flex items-center gap-2">
                    <Checkbox
                      checked={missingItems.includes(item)}
                      onCheckedChange={() => handleMissingItemToggle(item)}
                    />
                    <Label className="cursor-pointer">{item}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <Label>Additional Notes</Label>
              <Textarea
                placeholder="Additional feedback..."
                value={additionalNotes}
                onChange={(e) => setAdditionalNotes(e.target.value)}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestChangesModalOpen(false)}>Cancel</Button>
            <Button onClick={handleRequestChangesConfirm}>Send Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Listing Modal */}
      <Dialog open={rejectListingModalOpen} onOpenChange={setRejectListingModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Listing Request</DialogTitle>
            <DialogDescription>Please provide a reason for rejection</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              placeholder="Reason for rejecting listing request..."
              value={listingRejectionReason}
              onChange={(e) => setListingRejectionReason(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectListingModalOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleRejectListingConfirm}>Reject Listing</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Trainer</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete this trainer from the database?
              This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          {trainerToDelete && (
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded">
              <p className="font-medium">{trainerToDelete.name}</p>
              <p className="text-sm text-gray-600 dark:text-gray-400">{trainerToDelete.email}</p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteConfirm}>Delete Permanently</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profile History Comparison Modal */}
      <Dialog open={comparisonModalOpen} onOpenChange={setComparisonModalOpen}>
        <DialogContent className="max-w-7xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Profile Change History - Document Comparison</DialogTitle>
            <DialogDescription>Compare previous and current versions (Red = Old, Green = New)</DialogDescription>
          </DialogHeader>
          {loadingHistory ? (
            <div className="py-8 text-center">Loading history...</div>
          ) : profileHistory.length > 0 ? (
            <div className="space-y-6">
              {profileHistory.map((entry, idx) => {
                const isLatest = idx === 0;
                const hasPrevious = idx < profileHistory.length - 1;
                const previousEntry = hasPrevious ? profileHistory[idx + 1] : null;
                
                return (
                  <div key={idx} className="border dark:border-gray-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-4">
                      <Badge variant={isLatest ? "default" : "outline"}>
                        {new Date(entry.timestamp).toLocaleString()}
                      </Badge>
                      {entry.note && (
                        <span className="text-sm text-gray-600 dark:text-gray-400 italic font-medium">{entry.note}</span>
                      )}
                      {isLatest && (
                        <Badge className="bg-green-500 text-white">CURRENT</Badge>
                      )}
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Previous Version - Only show if not the last entry */}
                      {hasPrevious && previousEntry && (
                        <div>
                          <h4 className="font-semibold text-red-700 dark:text-red-400 mb-2 flex items-center gap-2">
                            ❌ Previous Version
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({new Date(previousEntry.timestamp).toLocaleDateString()})
                            </span>
                          </h4>
                          <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded space-y-2">
                            <div>
                              <Label className="text-xs text-gray-600 dark:text-gray-400">Bio</Label>
                              <p className="text-sm dark:text-gray-300">{previousEntry.bio || 'N/A'}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600 dark:text-gray-400">Price/Hour</Label>
                              <p className="text-sm">${previousEntry.pricePerHour || 'N/A'}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600 dark:text-gray-400">Specializations</Label>
                              <div className="flex flex-wrap gap-1 mt-1">
                                {previousEntry.specializations?.map((s, i) => (
                                  <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                                )) || <span className="text-xs text-gray-400">None</span>}
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600 dark:text-gray-400">City</Label>
                              <p className="text-sm dark:text-gray-300">{previousEntry.location?.city || 'N/A'}</p>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600 dark:text-gray-400">Photos ({previousEntry.photos?.length || 0})</Label>
                              <div className="grid grid-cols-2 gap-2 mt-2">
                                {previousEntry.photos?.slice(0, 4).map((photo, i) => (
                                  <img key={photo} src={photo} alt={`Previous photo ${i + 1}`} className="w-full h-20 object-cover rounded border-2 border-red-300 dark:border-red-700" />
                                ))}
                                {!previousEntry.photos?.length && (
                                  <p className="text-xs text-gray-400 col-span-2">No photos</p>
                                )}
                              </div>
                            </div>
                            <div>
                              <Label className="text-xs text-gray-600 dark:text-gray-400">Certificates ({previousEntry.certificates?.length || 0})</Label>
                              <div className="space-y-1 mt-2">
                                {previousEntry.certificates?.map((cert, i) => (
                                  <div key={cert.url || i} className="flex items-center gap-2 p-1 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                                    <FileText className="w-3 h-3 text-red-600" />
                                    <span className="text-xs truncate">{cert.filename || `Certificate ${i + 1}`}</span>
                                  </div>
                                ))}
                                {!previousEntry.certificates?.length && (
                                  <p className="text-xs text-gray-400">No certificates</p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Current/This Version */}
                      <div>
                        <h4 className="font-semibold text-green-700 dark:text-green-400 mb-2 flex items-center gap-2">
                          ✅ {isLatest ? 'Current Version (Latest)' : 'This Version'}
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            ({new Date(entry.timestamp).toLocaleDateString()})
                          </span>
                        </h4>
                        <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded space-y-2">
                        <div>
                          <Label className="text-xs text-gray-600 dark:text-gray-400">Bio</Label>
                          <p className="text-sm dark:text-gray-300">{entry.bio || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 dark:text-gray-400">Price/Hour</Label>
                          <p className="text-sm">${entry.pricePerHour || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 dark:text-gray-400">Specializations</Label>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {entry.specializations?.map((s, i) => (
                              <Badge key={s} variant="outline" className="text-xs">{s}</Badge>
                            ))}
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 dark:text-gray-400">City</Label>
                          <p className="text-sm dark:text-gray-300">{entry.location?.city || 'N/A'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 dark:text-gray-400">Photos ({entry.photos?.length || 0})</Label>
                          <div className="grid grid-cols-2 gap-2 mt-2">
                            {entry.photos?.slice(0, 4).map((photo, i) => (
                              <img key={photo} src={photo} alt={`Current photo ${i + 1}`} className="w-full h-20 object-cover rounded border-2 border-green-300 dark:border-green-700" />
                            ))}
                            {!entry.photos?.length && (
                              <p className="text-xs text-gray-400 col-span-2">No photos</p>
                            )}
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-600 dark:text-gray-400">Certificates ({entry.certificates?.length || 0})</Label>
                          <div className="space-y-1 mt-2">
                            {entry.certificates?.map((cert, i) => (
                              <div key={cert.url || i} className="flex items-center gap-2 p-1 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
                                <FileText className="w-3 h-3 text-green-600" />
                                <span className="text-xs truncate">{cert.filename || `Certificate ${i + 1}`}</span>
                              </div>
                            ))}
                            {!entry.certificates?.length && (
                              <p className="text-xs text-gray-400">No certificates</p>
                            )}
                          </div>
                        </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center py-8 text-gray-500 dark:text-gray-400">No history available</p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TrainerListManagerNew;
